import type { QueryTimeMode, TimeRange, TimeRangeValidateResult } from '../types'

/** 未设置时的占位文案 */
export const UNSET_TEXT = '- 未设置 -'
/** 不限时间的展示文案 */
export const UNLIMITED_TEXT = '不限'

/**
 * 可查询时间的校验与展示格式化。
 * 单个产品的编辑浮层与批量修改弹窗共用同一份实现，保证两处校验行为完全一致。
 */
export function useTimeRange() {
  /**
   * 校验时间段：
   * 1. 每段开始与结束日期都必填；
   * 2. 开始日期必须早于或等于结束日期；
   * 3. 多段之间不允许重叠，前一段结束日期与后一段开始日期相同也判定为重叠。
   * 校验通过时返回按开始日期升序排序后的结果。
   */
  const validateRanges = (ranges: TimeRange[]): TimeRangeValidateResult => {
    if (!ranges.length) {
      return { ok: false, message: '请填写完整的时间段' }
    }

    const incomplete = ranges.some((item) => !item.startDate || !item.endDate)
    if (incomplete) {
      return { ok: false, message: '请填写完整的时间段' }
    }

    const invalidOrder = ranges.some((item) => item.startDate > item.endDate)
    if (invalidOrder) {
      return { ok: false, message: '开始日期不能晚于结束日期' }
    }

    const sorted = ranges
      .map((item) => ({ ...item }))
      .sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0))

    for (let i = 1; i < sorted.length; i += 1) {
      // 相邻两段必须严格分离：前一段结束日期等于后一段开始日期即视为重叠
      if (sorted[i - 1].endDate >= sorted[i].startDate) {
        return { ok: false, message: '时间段不能重叠' }
      }
    }

    return { ok: true, ranges: sorted }
  }

  /** 单段的展示文案 */
  const formatRange = (range: TimeRange) => `${range.startDate} 至 ${range.endDate}`

  /** 列表单元格的主展示文案 */
  const formatQueryTime = (mode: QueryTimeMode, ranges: TimeRange[]) => {
    if (mode === 'unlimited') return UNLIMITED_TEXT
    if (mode === 'ranges' && ranges.length) return formatRange(ranges[0])
    return UNSET_TEXT
  }

  /** 多段时在主文案后追加的「共 N 段」标签，单段或无段时返回空字符串 */
  const formatRangeCount = (mode: QueryTimeMode, ranges: TimeRange[]) =>
    mode === 'ranges' && ranges.length > 1 ? `共 ${ranges.length} 段` : ''

  return {
    validateRanges,
    formatRange,
    formatQueryTime,
    formatRangeCount,
  }
}
